import { Injectable } from "@angular/core";
import { CoreOrderAudit } from "../../model/core/CoreOrderAudit";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { JSONMessagePipe } from "app/bfx-order-audit/pipes/bfx-json-message.pipe";
import { OrderActionPipe } from "app/bfx-order-audit/pipes/bfx-order-action.pipe";
import { BfxDatePipe } from "app/bfx-order-audit/pipes/bfx-date.pipe";
import { TranslateService } from "@ngx-translate/core";
import { SysFeatures, SystemConfigPrimaryService } from "shared/auth-services";
import { CoreBenchmarkAudit } from "shared/cubiq-services/model/core/CoreBenchmarkAudit";
import { CoreAudit } from "shared/cubiq-services/model/core/CoreAudit";
const logger = LoggerService.for(__dirname);

const LIST_OF_MODIFY_IN_ACTIONS: Set<string> = new Set<string>([
  "ORDACT_MODIFY_IN_ACCEPT_NEEDED",
  "ORDACT_MODIFY_IN_TRDR_ACCEPTED",
]);

const LIST_OF_FILL_NOW_ACTIONS: Set<string> = new Set<string>(["ORDACT_FILL_NOW"]);

/**
 * Share structure for Order and BM Audits
 */
type CommonCubiqAuditType = {
  auditId: CubiqTS.Long | null;
  userId: CubiqTS.Long | null;
  userCd: string;
  userName: string;
  comments: string;
  actionTime: CubiqTS.Long | null;
  orderState: CubiqTS.OrderState;
  orderAction: string;
};

@Injectable()
export class OrderAuditAdapter {
  protected enabledOBO: boolean = false;

  private currentRowId: number = 1;

  public jsonPipe: JSONMessagePipe;

  public actionPipe: OrderActionPipe;

  public bfxDatePipe: BfxDatePipe;

  private showUserName: boolean;

  constructor(
    protected translationService: TranslateService,
    protected configService: BfxConfigService,
    systemConfigService: SystemConfigPrimaryService,
  ) {
    this.jsonPipe = new JSONMessagePipe(this.translationService);
    this.actionPipe = new OrderActionPipe();
    this.bfxDatePipe = new BfxDatePipe();
    configService.getUIConfig().then((config) => {
      this.enabledOBO = config && config.general ? config.general.oboEnabled : false;
    });
    systemConfigService.getSystemConfig(SysFeatures.displayUserNames).then((value) => {
      this.showUserName =
        value == "true" || value == "TRUE" || value == "1" || value == "Y" || value == "y";
    });
  }

  /**
   * Create Audit view model for Order audit
   * @param orderAudit
   * @returns
   */
  public transformOrderAudit(orderAudit: CubiqTS.OrderAuditImpl): CoreOrderAudit {
    let result: CoreOrderAudit = null;

    if (orderAudit) {
      result = new CoreOrderAudit();
      this.populateCoreAudit(result, orderAudit);

      result.rawAudit = orderAudit;
      result.actionSuccess = orderAudit.actionSuccess;
      if (orderAudit.orderId) {
        result.orderId = orderAudit.orderId.toString();
      }
      result.userStr =
        result.userStr +
        (this.enabledOBO && orderAudit.oboUser != null && orderAudit.oboUser.trim().length > 0
          ? `/${orderAudit.oboUser}`
          : "");

      if (orderAudit.traderGroupId) {
        result.traderGroupId = orderAudit.traderGroupId.toString();
      }
    }

    return result;
  }

  /**
   * Create Audit view model for BM (Fixing) audit - mainly subset of Order functionality
   * @param bmAudit
   * @returns
   */
  public transformBenchmarkAudit(bmAudit: CubiqTS.BenchmarkPositionAudit): CoreBenchmarkAudit {
    let result: CoreBenchmarkAudit = null;

    if (bmAudit) {
      result = new CoreBenchmarkAudit();
      this.populateCoreAudit(result, bmAudit);
    }

    return result;
  }

  /**
   * Popuplate CoreAudit view model with common audit properties
   * This is used by both Order and BM audits
   * @param result
   * @param orderAudit
   */
  private populateCoreAudit(result: CoreAudit, orderAudit: CommonCubiqAuditType) {
    result.rowId = this.currentRowId++;
    result.actionTime = orderAudit.actionTime.toNumber();
    result.actionTimeStr = CubiqTS.moment(result.actionTime)
      .local()
      .format("DD-MMM-YY HH:mm:ss.SSS");

    result.comments = this.translateComments(orderAudit.comments);
    result.orderAction = this.translateOrderAction(orderAudit.orderAction);
    result.orderStatus = this.translateAuditOrderStatus(orderAudit);

    result.userStr = this.showUserName ? orderAudit.userName : orderAudit.userCd;
  }

  private translateComments(comments: string): string[] {
    const retComments = [];
    if (typeof comments === "string" && comments.length > 0) {
      let message: string[] = JSON.parse(comments);
      message.forEach((element) => {
        if (typeof element["id"] === "string" && element["id"].length > 0) {
          try {
            const translatedString = this.translationService.instant(
              element["id"],
              element["args"],
            );
            retComments.push(translatedString);
          } catch (ex) {
            logger.out.error(
              `ERROR: JSONMessagePipe(ORDER): element: ${element} | exception: ${ex}`,
            );
            retComments.push(element["id"]);
          }
        }
      });
    }
    return retComments;
  }

  /**
   * Determine the correct order status translation t apply to input order state
   * @param orderAudit
   * @returns
   */
  private translateAuditOrderStatus(orderAudit: CommonCubiqAuditType): string {
    let retStatus: string;
    if (orderAudit.orderState === null || orderAudit.orderState === undefined) {
      retStatus = "";
    } else {
      const strStatus = CubiqTS.OrderState[orderAudit.orderState];
      if (orderAudit.orderAction && LIST_OF_MODIFY_IN_ACTIONS.has(orderAudit.orderAction)) {
        retStatus = this.translateOrderStatusFromAction(orderAudit.orderAction, strStatus);
      } else if (orderAudit.orderAction && LIST_OF_FILL_NOW_ACTIONS.has(orderAudit.orderAction)) {
        retStatus = this.translateOrderStatusFromAction(orderAudit.orderAction, strStatus);
      } else {
        retStatus = this.translateOrderStatus(strStatus);
      }
    }
    return retStatus;
  }

  /**
   * Simple translate from order state to text
   * @param status
   * @returns
   */
  private translateOrderStatus(status: string): string {
    const lookupKey = status;
    return this.translateWithErrorHandling(lookupKey);
  }

  /**
   * Audit action-specifc translation of order state - used for specific modify actions
   * @param orderAction
   * @param status
   * @returns
   */
  private translateOrderStatusFromAction(orderAction: string, status: string): string {
    const actionKey = orderAction.toLowerCase().replace(/_/g, ".");
    const lookupKey = `${status}.${actionKey}`;
    return this.translateWithErrorHandling(lookupKey);
  }

  private translateOrderAction(actionCode: string): string {
    if (actionCode) {
      const lookupKey = actionCode.toLowerCase().replace(/_/g, ".");
      return this.translateWithErrorHandling(lookupKey);
    }
    return "<UNKNOWN>";
  }

  private translateWithErrorHandling(value: string): string {
    let result = null;
    try {
      result = value != null ? this.translationService.instant(value) : "<UNKNOWN>";
    } catch (ex) {
      logger.out.error(`ERROR: translateWithErrorHandling(): Missing translation key for ${value}`);
      result = value;
    }
    return result;
  }
}
